// Schedule pickup, Reschedule and Mark shipped as a page starts them (the Worklist's action bar and
// Ship column, the Shipping schedule's rows and pickups): which lines may go, the dialog, then the
// write. One place, so the Worklist and the Shipping page refuse the same things.

import { useState } from "react";
import {
  checkReschedule,
  countText,
  lineWords,
  openPickups,
  rescheduleStart,
  scheduleRows,
  scheduledText,
  toSchedule,
  lineRef,
} from "@app/shared";
import type { LineView, PickupRecord, RescheduleStart, RoutingPayload, ScheduleRow } from "@app/shared";
import { useCarriers } from "./data";
import { notify, useMessage } from "./message";
import type { CarrierSourceApi } from "./pickers";
import { MarkShippedDialog, RescheduleChoiceDialog, SchedulePickupDialog } from "./shipping-dialogs";
import { useShippingWriter } from "./shipping-writer";
import type { Writer } from "./writer";

export function useShipFlow({
  writer,
  payload,
  lines,
  today,
  onDone,
}: {
  writer: Writer;
  payload: RoutingPayload | undefined;
  /** Every line of the page (`assemble`'s), open and finished. */
  lines: readonly LineView[];
  today: string;
  onDone?: () => void;
}) {
  const message = useMessage();
  const shipping = useShippingWriter(writer);
  /** Schedule pickup's lines; `date`: opened from the Worklist's Appointment date cell, with that date. */
  const [scheduling, setScheduling] = useState<{
    lines: LineView[];
    date?: string;
    undoable?: boolean;
    /** Opened from a cell: when the dialog closes, cancelled or done (the focus back to the grid). */
    onClosed?: () => void;
  } | null>(null);
  const [toShip, setToShip] = useState<{ lines: LineView[]; pickup: PickupRecord | null } | null>(null);
  /** Reschedule: the selected rows and their pickups; `how` once known (asked when only some lines of a pickup are). */
  const [rescheduling, setRescheduling] = useState<{
    rows: ScheduleRow[];
    start: RescheduleStart;
    how: "whole" | "move" | null;
    /** From the Appointment date cell: the new date, and what to do when the dialog closes. */
    date?: string;
    onClosed?: () => void;
  } | null>(null);
  const closeScheduling = () => {
    scheduling?.onClosed?.();
    setScheduling(null);
  };
  const closeRescheduling = () => {
    rescheduling?.onClosed?.();
    setRescheduling(null);
  };

  const rows = scheduleRows(lines);
  const rowsOf = (pickupId: string) => rows.filter((r) => r.pickup.id === pickupId);
  // The carriers of A2000 and Deposco (item 8): their list only, never a typed one; unavailable,
  // the fields say so and the forms go on with no carrier.
  const carrierQuery = useCarriers();
  const carriers: CarrierSourceApi = {
    options: carrierQuery.data?.options ?? [],
    status: carrierQuery.isError ? "failed" : carrierQuery.data ? "ready" : "loading",
    retry: () => void carrierQuery.refetch(),
  };
  const carrierRows = carrierQuery.data?.rows ?? [];
  const upcoming = openPickups(payload?.pickups ?? [], (id) => rowsOf(id).length, today);

  /** Only open lines in the WMS; the big red message names why not. */
  const openOnly = async (ls: readonly LineView[], what: string) => {
    const not = ls.filter((l) => l.finished || l.noWms);
    if (!not.length && ls.length) return true;
    await message.error(
      `Can't ${what} these lines`,
      ls.length ? "Only open Worklist lines with a WMS # can go. Leave these out:" : "Select one or more lines first.",
      not.map(
        (l) => `${l.customerName} · ${lineRef(l) || "no WMS # yet"}${l.finished ? ` · ${l.finished.status}` : ""}`,
      ),
    );
    return false;
  };

  return {
    /** The rows of each pickup, for the Shipping page. */
    rows,
    /** The carriers of A2000 and Deposco, for every carrier field. */
    carriers,
    shipping,
    /**
     * Schedule pickup (or Add to an existing pickup) for these lines: those already on a pickup are
     * left where they are — the orange "Are you sure?" lists them, and only the others go.
     */
    async schedule(ls: readonly LineView[], opts: { date?: string; undoable?: boolean; onClosed?: () => void } = {}) {
      if (!(await openOnly(ls, "schedule"))) return opts.onClosed?.();
      const { done, todo } = toSchedule(ls);
      if (!todo.length) {
        opts.onClosed?.();
        return notify("Already scheduled", "Every selected line is on a pickup.");
      }
      if (done.length) {
        const ok = await message.confirm(
          "Some lines are already scheduled — are you sure?",
          `${countText(done.length, "line")} ${done.length === 1 ? "is" : "are"} on a pickup already and stay${done.length === 1 ? "s" : ""} there. Only the others are scheduled.`,
          {
            lines: [
              ...done.map((l) => `Already scheduled: ${scheduledText(l)}`),
              ...todo.map((l) => `Will be scheduled: ${lineWords(l)}`),
            ],
            confirmLabel: `Schedule the ${countText(todo.length, "other")}`,
          },
        );
        if (!ok) return opts.onClosed?.();
      }
      setScheduling({ lines: todo, ...opts });
    },
    /**
     * Reschedule (item 2) the pickup(s) of these Shipping schedule rows: the whole pickup, or — when
     * only some of its lines are selected, and the person says so — those lines to a new pickup.
     */
    reschedule(selected: readonly ScheduleRow[], date?: string, onClosed?: () => void) {
      if (!selected.length) return onClosed?.();
      const start = rescheduleStart(selected, rows);
      setRescheduling({ rows: [...selected], start, how: start.partial ? null : "whole", date, onClosed });
    },
    /** Mark shipped: lines of one pickup (the whole truck, or some of it), or lines on none. */
    async markShipped(ls: readonly LineView[]) {
      if (!(await openOnly(ls, "mark shipped"))) return;
      const pickups = new Set(ls.map((l) => l.onPickup?.pickup.id ?? ""));
      if (pickups.size > 1)
        return message.error(
          "Mark shipped one pickup at a time",
          "These lines are on different pickups, or some are on none. Select the lines of one pickup (or of none) and mark them shipped.",
        );
      setToShip({ lines: [...ls], pickup: ls[0]?.onPickup?.pickup ?? null });
    },
    dialogs: (
      <>
        {scheduling ? (
          <SchedulePickupDialog
            lines={scheduling.lines}
            pickups={upcoming}
            carriers={carriers}
            carrierRows={carrierRows}
            date={scheduling.date}
            today={today}
            onClose={closeScheduling}
            onSchedule={(target) => {
              const { lines: ls, undoable } = scheduling;
              closeScheduling();
              void shipping.schedule(ls, target, "schedule", undoable).then((ok) => ok && onDone?.());
            }}
          />
        ) : null}
        {rescheduling && !rescheduling.how ? (
          <RescheduleChoiceDialog
            selected={rescheduling.rows.length}
            onPickups={rescheduling.start.pickups.reduce((n, p) => n + rowsOf(p.id).length, 0)}
            onClose={closeRescheduling}
            onChoose={(how) => setRescheduling({ ...rescheduling, how })}
          />
        ) : null}
        {rescheduling?.how ? (
          <SchedulePickupDialog
            lines={
              rescheduling.how === "move"
                ? rescheduling.rows.map((r) => r.line)
                : rescheduling.start.pickups.flatMap((p) => rowsOf(p.id).map((r) => r.line))
            }
            pickups={upcoming}
            carriers={carriers}
            carrierRows={carrierRows}
            today={today}
            onClose={closeRescheduling}
            onSchedule={() => {}}
            reschedule={{
              how: rescheduling.how,
              prefill: rescheduling.date
                ? { ...rescheduling.start.prefill, pickupDate: rescheduling.date }
                : rescheduling.start.prefill,
              several: rescheduling.start.several,
              check: (typed) =>
                checkReschedule(
                  rescheduling.start,
                  typed,
                  rescheduling.how!,
                  {
                    selected: rescheduling.rows.map((r) => r.line),
                    onPickup: (id) => rowsOf(id).map((r) => r.line),
                  },
                  today,
                ),
              onReschedule: (typed) => {
                const r = rescheduling;
                closeRescheduling();
                const saved =
                  r.how === "move"
                    ? shipping.moveLines(r.rows, typed, rowsOf)
                    : shipping.reschedule(r.start.pickups, typed, r.start.several, rowsOf);
                void saved.then((ok) => ok && onDone?.());
              },
            }}
          />
        ) : null}
        {toShip ? (
          <MarkShippedDialog
            lines={toShip.lines}
            pickup={toShip.pickup}
            carriers={carriers}
            today={today}
            onClose={() => setToShip(null)}
            onShip={(input) => {
              const ls = toShip.lines;
              setToShip(null);
              void shipping.ship(ls, input, rowsOf).then((ok) => ok && onDone?.());
            }}
          />
        ) : null}
      </>
    ),
  };
}
